/*
=========================================================
* Material Kit 2 React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-kit-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import StepContent from "@mui/material/StepContent";

// Material Kit 2 React components
import MKBox from "../../components/MKBox";
import MKTypography from "../../components/MKTypography";

// Material Kit 2 React examples
import DefaultNavbar from "../../examples/Navbars/DefaultNavbar";
import DefaultFooter from "../../examples/Footers/DefaultFooter";

// Sections
import Features from "../EmtayPage/sections/Features";
import ContactUs from "../EmtayPage/sections/ContactUs";

// Routes
import routeKeys from "../../routeKeys";

// i18n
import { useLanguage } from "../../i18n";

import { legalbar } from "../EmtayPage/sections/legalbar";

// Images
import bgImage from "../../assets/images/emtay/bg-emtay.webp";

function OurMethodPage() {
  const { t } = useLanguage();

  // Translate routes
  const routes = routeKeys.map((route) => ({
    ...route,
    name: t(route.key),
  }));

  const steps = [
    {
      label: t("method.intakeTitle"),
      description: t("method.intakeDesc"),
    },
    {
      label: t("method.offerteTitle"),
      description: t("method.offerteDesc"),
    },
    {
      label: t("method.phasesTitle"),
      description: t("method.phasesDesc"),
    },
  ];

  return (
    <>
      <a name="top" />
      <DefaultNavbar routes={routes} transparent light />
      <MKBox
        width="100%"
        sx={{
          minHeight: { xs: "26rem", md: "22rem" },
          height: { xs: "48vh", md: "40vh" },
          maxHeight: { xs: "38rem", md: "34rem" },
          backgroundImage: `url(${bgImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          display: "grid",
          placeItems: "center",
        }}
      >
        <Container>
          <Grid
            container
            item
            xs={12}
            lg={8}
            justifyContent="center"
            alignItems="center"
            flexDirection="column"
            sx={{
              mx: "auto",
              textAlign: "center",
              pt: { xs: 10, sm: 11, md: 12, lg: 0 },
            }}
          >
            <MKTypography variant="h2" color="white">
              {t("method.title")}
            </MKTypography>
            <MKTypography variant="body1" color="white" opacity={0.8} mt={1} mb={3}>
              {t("method.subtitle")}
            </MKTypography>
          </Grid>
        </Container>
      </MKBox>
      <Card
        sx={{
          p: 2,
          mx: { xs: 2, lg: 3 },
          mt: -8,
          mb: 2,
          boxShadow: ({ boxShadows: { xxl } }) => xxl,
        }}
      >
        <Container sx={{ py: 4 }}>
          <Grid container spacing={4}>
            <Grid item xs={12} lg={6}>
              <MKTypography variant="h4" my={3}>
                {t("method.title")}
              </MKTypography>
              <MKTypography variant="body2" color="text" mb={4}>
                {t("method.intro")}
              </MKTypography>
              <Stepper
                orientation="vertical"
                sx={{
                  "& .MuiStepLabel-iconContainer": {
                    ml: "5px",
                    mt: "6px",
                  },
                  "& .MuiStepContent-root": {
                    borderLeft: "2px solid #e0e0e0",
                    ml: "11px",
                    minHeight: "auto",
                  },
                  "& .MuiStepConnector-line": {
                    minHeight: "auto",
                    borderLeft: "2px solid #e0e0e0",
                    ml: "11px",
                  },
                  "& .MuiStepConnector-root": {
                    minHeight: "auto",
                  },
                }}
              >
                {steps.map((step, index) => (
                  <Step key={index} active>
                    <StepLabel
                      sx={{
                        "& .MuiStepLabel-labelContainer": {
                          pl: "7px",
                        },
                      }}
                    >
                      <MKTypography
                        component="span"
                        variant="h5"
                        sx={{ display: "block", color: "#fcfcfc" }}
                      >
                        {step.label}
                      </MKTypography>
                    </StepLabel>
                    <StepContent>
                      <MKTypography variant="body2" color="dark" sx={{ color: "#fafafa" }}>
                        {step.description}
                      </MKTypography>
                    </StepContent>
                  </Step>
                ))}
              </Stepper>
            </Grid>
            <Grid item xs={12} lg={6}>
              <MKBox
                sx={{
                  bgcolor: "#f8f9fa",
                  p: 3,
                  borderRadius: 2,
                  mt: { xs: 0, lg: 8 },
                  borderRight: "3px solid #1976d2",
                }}
              >
                <MKTypography variant="h5" my={2}>
                  {t("method.dbaTitle")}
                </MKTypography>
                <MKTypography variant="body2" color="dark" mb={2} sx={{ color: "#333" }}>
                  {t("method.dbaDesc")}
                </MKTypography>
                <MKTypography variant="body2" color="dark" sx={{ color: "#333" }}>
                  {t("method.dbaExplanation")}
                </MKTypography>
                <MKTypography
                  variant="body2"
                  color="dark"
                  mt={2}
                  sx={{ color: "rgb(93, 98, 124)", fontWeight: 300, fontSize: "0.9rem" }}
                >
                  {t("method.dbaFineprint")}
                </MKTypography>
              </MKBox>
            </Grid>
          </Grid>
        </Container>

        <a name="services" />
        <Features />

        <a name="contact" />
        <ContactUs isThanks={false} />
      </Card>
      <MKBox pt={2} px={1} mt={0} py={0}>
        <DefaultFooter content={{ copyright: legalbar }} />
      </MKBox>
    </>
  );
}

export default OurMethodPage;
